// src/playback-runtime/lifecycle/useBackgroundAudioGuard.ts

"use client";

/*
 Prevent browser from suspending audio
 when screen locks / tab hidden
*/

import { useEffect, useRef } from "react";

export function useBackgroundAudioGuard(
  audioRef: React.RefObject<HTMLAudioElement | null>
) {
  const wakeLockRef = useRef<any>(null);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    let visibilityHandler: any;

    /* ---------- Android Chrome wake lock ---------- */
    const requestWakeLock = async () => {
      try {
        // not supported everywhere
        // prevents CPU sleep
        if ("wakeLock" in navigator && document.visibilityState === "visible") {
          // @ts-ignore
          wakeLockRef.current = await navigator.wakeLock.request("screen");
        }
      } catch {}
    };

    const releaseWakeLock = async () => {
      try {
        await wakeLockRef.current?.release();
        wakeLockRef.current = null;
      } catch {}
    };

    /* ---------- when playback starts ---------- */
    const onPlay = async () => {
      await requestWakeLock();
    };

    /* ---------- when paused ---------- */
    const onPause = async () => {
      await releaseWakeLock();
    };

    /* ---------- tab hidden ---------- */
    visibilityHandler = async () => {
      if (document.visibilityState === "hidden") {
        // keep audio alive — DO NOTHING
        return;
      }

      // visible again
      if (!el.paused) await requestWakeLock();
    };

    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    document.addEventListener("visibilitychange", visibilityHandler);

    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      document.removeEventListener("visibilitychange", visibilityHandler);
      releaseWakeLock();
    };
  }, [audioRef]);
}
